import { useMedplum } from '@medplum/react';
import { Patient } from '@medplum/fhirtypes';
import { Anchor, Stack, Text, UnstyledButton } from '@mantine/core';
import { useEffect, useState } from 'react';
import { canSearchResourceType } from '@/services/permissions';
import { alert } from '@/services/alert';
import { reportError } from '@/services/error-reporting';
import { ORBIT_GRAY } from '@/theme/colors';
import type { SubscriberInput } from '../../../../../../bots/src/handlers/update-insurance-coverage';
import { insuranceRows } from '@/pages/PatientTimelinePage/PatientSummary/Insurance/insuranceDetails';
import {
  canWriteSubscriber,
  subscriberFormValues,
} from '@/pages/PatientTimelinePage/PatientSummary/Insurance/subscriber';
import { SubscriberForm } from '@/pages/PatientTimelinePage/PatientSummary/Insurance/SubscriberForm';
import { useInsuranceSection } from '@/pages/PatientTimelinePage/PatientSummary/Insurance/useInsuranceSection';
import { Row } from './PatientAccessCallWidgets';

/** No Patient yet: nothing is read until one loads. */
const NO_PATIENT: Patient = { resourceType: 'Patient' };

interface PolicyholderRowProps {
  label: string;
  say?: string;
  patient: Patient | undefined;
  /** Whose plan, as the Coverage records it: the patient's relationship to the policyholder. */
  relationship: 'self' | 'spouse' | 'child';
  /** The caller may write the insurance card. The bot's own check is the boundary. */
  editable: boolean;
}

/**
 * The policyholder on the PN card v2.0 (BAR-2998), read from the insurance
 * Coverage and written to its subscriber through `update-insurance-coverage`:
 * the same read, form and write as the Insurance section's Subscriber row
 * (BAR-2933). Never stored in the note.
 *
 * A spouse's, partner's or parent's plan takes the policyholder's legal name
 * and date of birth; the relationship comes from Whose plan, so the form
 * doesn't ask it again. The patient's own plan shows only when the Coverage
 * still names someone else, with one action to make it their own.
 */
export function PolicyholderRow({ label, say, patient, relationship, editable }: PolicyholderRowProps) {
  const medplum = useMedplum();
  const section = useInsuranceSection(patient ?? NO_PATIENT, Boolean(patient?.id));
  const [editing, setEditing] = useState(false);
  // A change of Whose plan starts over: no edit left open for another relationship.
  useEffect(() => setEditing(false), [relationship]);
  const [saving, setSaving] = useState(false);
  const { state } = section;

  const save = async (subscriber: SubscriberInput): Promise<void> => {
    try {
      await section.save({ subscriber });
      setEditing(false);
    } catch (error) {
      reportError(error, { component: 'PolicyholderRow' });
      alert.error('The policyholder could not be saved to the insurance card.');
      throw error;
    }
  };

  // A role that can't search Coverage sees no row, as the insurance card rows.
  if (state.status === 'forbidden') return null;

  let body;
  if (state.status === 'loading') {
    if (relationship === 'self') return null;
    body = <Muted>Reading the insurance card…</Muted>;
  } else if (state.status === 'none') {
    if (relationship === 'self') return null;
    body = <Muted>No insurance card on file until they move to the Insurance track</Muted>;
  } else if (state.status !== 'found') {
    if (relationship === 'self') return null;
    body = <Muted>Couldn’t read the insurance card</Muted>;
  } else {
    const { details } = state;
    const shown =
      details.relationship && details.relationship !== 'self' ? insuranceRows(details).subscriber.text : undefined;
    const canWrite = canWriteSubscriber(
      editable,
      canSearchResourceType(medplum, 'RelatedPerson'),
      details.relationship,
      details.policyholder
    );

    if (relationship === 'self') {
      // Their own plan: nothing to record unless the Coverage still names someone else.
      if (!shown) return null;
      body = (
        <Stack gap={2} align="flex-start">
          <Text size="sm">{shown}</Text>
          {canWrite && (
            <Anchor
              component="button"
              type="button"
              size="sm"
              c="green.9"
              disabled={saving}
              data-testid="pn-policyholder-make-own"
              onClick={async () => {
                setSaving(true);
                await save({ relationship: 'self' }).catch(() => undefined);
                setSaving(false);
              }}
            >
              Save as their own plan
            </Anchor>
          )}
        </Stack>
      );
    } else {
      // The policyholder on file, when the Coverage already records this relationship.
      const onFile = details.relationship === relationship ? details.policyholder : undefined;
      // Said as on file whenever the Coverage records this relationship, even if
      // its policyholder didn't load, as the Insurance section's row says it.
      const onFileText = details.relationship === relationship ? shown : undefined;
      if (canWrite && (editing || !onFile)) {
        body = (
          <SubscriberForm
            // A new form when Whose plan changes: it holds its values from the first render.
            key={relationship}
            initial={subscriberFormValues(relationship, onFile)}
            relationshipLocked
            onSave={save}
            onCancel={onFile ? () => setEditing(false) : undefined}
          />
        );
      } else if (onFileText && canWrite) {
        body = (
          <UnstyledButton onClick={() => setEditing(true)} data-testid="pn-policyholder-on-file">
            <Text size="sm">{onFileText}</Text>
          </UnstyledButton>
        );
      } else {
        body = onFileText ? <Text size="sm">{onFileText}</Text> : <Muted>Not on file</Muted>;
      }
    }
  }

  return (
    <Row label={label} say={say}>
      {body}
    </Row>
  );
}

function Muted({ children }: { children: string }) {
  return (
    <Text size="sm" c={ORBIT_GRAY}>
      {children}
    </Text>
  );
}
